import { parseGermanFloat } from './germanFormats';

// As there are fields of type string, that actually contain something else,
// this group of utils intends to provide parsing certain checks
// that also take care of the parsing necessary

/**
 * Compares two number strings, whether one is higher than or equal to the other
 * @param value the value that should be higher or equal
 * @param valueToCompareTo the value that should be lower or equal
 * @returns {boolean} whether `value` is higher than or equal to `valueToCompareTo`
 */
export function isHigherThanOrEqual(
    value: string | null,
    valueToCompareTo: string | null,
): boolean {
    const parsedValue = parseGermanFloat(value || '0');
    const parsedValueToCompareTo = parseGermanFloat(valueToCompareTo || '0');

    return parsedValue >= parsedValueToCompareTo;
}

export function sanitizeTextInputValue(value: string | null): string {
    if (!value) {
        return '';
    }

    value = String(value);

    if (value.normalize) {
        value = value.normalize('NFKC');
    }

    // Remove control characters except for \n \r \t
    value = value
        .split('')
        .filter((ch) => {
            const code = ch.charCodeAt(0);
            return !(code <= 0x1f && ch !== '\n' && ch !== '\r' && ch !== '\t');
        })
        .join('');

    if (!/[<>]|javascript|onerror|onload/i.test(value)) {
        return value;
    }

    // 1. HTML decode via temp. textarea (to surface hidden encoded tags)
    const ta = document.createElement('textarea');
    ta.innerHTML = value;
    let work = ta.value;

    // 2. Remove <script>...</script> blocks
    work = work.replace(/<script.*?>.*?<\/script>/gis, '');

    // 3. Remove event handler attributes (onXXX="..."/onXXX='...')
    //    We first neutralize them inside tags, then strip remaining tags later.
    work = work.replace(/on\w+\s*=\s*(['"]).*?\1/gis, '');

    // 4. Neutralize javascript: pseudo protocols
    work = work.replace(/javascript\s*:/gi, '');

    // 5. Remove iframes, objects, embeds entirely
    work = work.replace(
        /<(iframe|object|embed|meta|link|style).*?>.*?<\/\1>/gis,
        '',
    );

    // 6. Strip all remaining tags (since fields are plain text)
    work = work.replace(/<(iframe|object|embed|meta|link|style).*?\/>/gis, '');

    // 7. Collapse whitespace and trim
    work = work.replace(/<[^>]*>/g, '');
    work = work.replace(/\s{2,}/g, ' ').trim();

    // 8. (Optional) Length clamp to prevent storage of extremely large payloads with obfuscated content.
    const maxLen = 4000;
    if (work.length > maxLen) {
        work = work.substring(0, maxLen);
    }

    return work;
}

/**
 * Converts a phone number to digits only, replacing "+" with "00".
 * E.g. "(+49) 40 55667788" → "00494055667788"
 */
export function phoneNumberToDigits(phone: string | null): string {
    if (!phone) {
        return '';
    }
    return phone.replace(/\+/g, '00').replace(/\D/g, '');
}
